import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CUTS = {
  '30s': { label: '30s Hero', desc: 'Full story — bill shock → rigged rates → dream home → free AI design → trusted crew → freedom', scenes: 6, runtime: '~31s' },
  '15s': { label: '15s Social', desc: 'Feed cut — bill shock → dream home → trusted crew → freedom', scenes: 4, runtime: '~17s' },
  '6s': { label: '6s Bumper', desc: 'Pre-roll — dream home → freedom → free design end card', scenes: 2, runtime: '~8s' },
};

const SCRIPT = [
  { t: '00:00 — Hook', line: 'Your electric bill went up again. Rates rise, and you just pay.' },
  { t: 'Dream', line: 'What if you owned your power instead? Your roof. Your electricity.' },
  { t: 'Offer', line: 'Samaritan Solar designs a system on your actual roof — free — and shows every dollar you\u2019ll save over 25 years.' },
  { t: 'Proof', line: 'Twenty years of installs. A 25-year warranty. Little to no upfront cost.' },
  { t: 'Payoff', line: 'Stop renting your electricity. Own your power.' },
  { t: 'End card', line: 'Get your FREE roof design · (833) 207-0707 · samaritansolar.com' },
];

export default function AdShowcase() {
  const [cut, setCut] = useState('30s');
  const [aspect, setAspect] = useState('wide');
  const src = `/ad/samaritan_${cut}_${aspect}.mp4`;
  const poster = `/ad/poster_${cut}_${aspect}.jpg`;
  const srt = `/ad/samaritan_${cut}_captions.srt`;

  return (
    <div className="min-h-screen text-white" style={{ background: '#0C2417' }} data-testid="ad-showcase-page">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between" data-testid="ad-nav">
        <Link to="/landing" className="flex items-center gap-3">
          <img src="/samaritan-logo.png" alt="Samaritan Solar" className="h-8" />
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold tracking-[0.25em] px-3 py-1.5 rounded-full border border-white/20 text-white/70" data-testid="ad-kit-badge">HOMEOWNER AD · 3 CUTS · 2 ASPECTS</span>
          <Link to="/landing" className="text-sm font-semibold px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-colors" data-testid="ad-back-btn">Back to site</Link>
        </div>
      </nav>

      <header className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-8 text-center">
        <p className="text-[11px] font-bold tracking-[0.3em]" style={{ color: '#FFDB01' }} data-testid="ad-overline">SAMARITAN SOLAR · “OWN YOUR POWER”</p>
        <h1 className="font-heading font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl mt-3" data-testid="ad-title">
          Your bill went up. Again.<br /><span style={{ color: '#FAAB1C' }}>Stop renting your electricity.</span>
        </h1>
        <p className="text-white/70 mt-4 max-w-2xl mx-auto" data-testid="ad-sub">The jaw-dropping homeowner spot for Samaritan Solar — bill shock, the rigged-rate villain, the dream home, a FREE AI roof design, and the freedom payoff. Pick a cut, hit play.</p>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
        <section className="rounded-[24px] overflow-hidden border border-white/10 bg-black/40" data-testid="ad-player-card">
          <div className="flex flex-wrap items-center gap-2 p-4 border-b border-white/10">
            {Object.entries(CUTS).map(([k, c]) => (
              <button key={k} onClick={() => setCut(k)} data-testid={`ad-cut-${k}`}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${cut === k ? 'text-[#0C2417]' : 'border border-white/20 text-white/80 hover:bg-white/10'}`}
                style={cut === k ? { background: '#FAAB1C' } : {}}>{c.label}</button>
            ))}
            <div className="ml-auto flex rounded-full border border-white/20 overflow-hidden">
              {['wide', 'vertical'].map((a) => (
                <button key={a} onClick={() => setAspect(a)} data-testid={`ad-aspect-${a}`}
                  className={`px-4 py-2 text-sm font-bold ${aspect === a ? 'bg-white text-[#0C2417]' : 'text-white/70'}`}>{a === 'wide' ? '16:9 Wide' : '9:16 Vertical'}</button>
              ))}
            </div>
          </div>
          <div className="grid place-items-center bg-black p-4 sm:p-6">
            <video key={src} src={src} poster={poster} controls playsInline preload="metadata"
              className={`rounded-xl bg-black ${aspect === 'wide' ? 'w-full aspect-video' : 'h-[62vh] aspect-[9/16]'}`}
              data-testid="ad-player" />
          </div>
          <div className="flex flex-wrap items-center gap-2 p-4 border-t border-white/10 text-sm">
            <span className="text-white/60 text-xs font-bold tracking-widest" data-testid="ad-now-playing">NOW PLAYING: {CUTS[cut].label} · {aspect.toUpperCase()} · {CUTS[cut].runtime}</span>
            <span className="ml-auto flex gap-2">
              <a href={src} download data-testid="ad-download-mp4" className="px-4 py-2 rounded-full font-bold text-xs text-[#0C2417]" style={{ background: '#FAAB1C' }}><i className="fa-solid fa-download mr-1" />MP4</a>
              <a href={poster} download data-testid="ad-download-poster" className="px-4 py-2 rounded-full font-bold text-xs border border-white/20 hover:bg-white/10"><i className="fa-solid fa-image mr-1" />Poster</a>
              <a href={srt} download data-testid="ad-download-srt" className="px-4 py-2 rounded-full font-bold text-xs border border-white/20 hover:bg-white/10"><i className="fa-solid fa-closed-captioning mr-1" />Captions</a>
            </span>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="rounded-[24px] border border-white/10 bg-white/5 p-6" data-testid="ad-script-card">
            <p className="text-[11px] font-bold tracking-[0.25em]" style={{ color: '#FFDB01' }}>VOICEOVER SCRIPT</p>
            <ul className="mt-3 space-y-3">
              {SCRIPT.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm" data-testid={`ad-script-${i}`}>
                  <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded-md bg-white/10 text-white/70 h-fit">{s.t}</span>
                  <span className="text-white/85">{s.line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[24px] border border-white/10 bg-white/5 p-6" data-testid="ad-cuts-card">
            <p className="text-[11px] font-bold tracking-[0.25em]" style={{ color: '#FFDB01' }}>ALL 6 FILES</p>
            <div className="mt-3 grid gap-2">
              {Object.keys(CUTS).map((k) => ['wide', 'vertical'].map((a) => (
                <a key={`${k}-${a}`} href={`/ad/samaritan_${k}_${a}.mp4`} data-testid={`ad-file-${k}-${a}`}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-sm transition-colors ${k === cut && a === aspect ? 'border-[#FAAB1C] bg-[#FAAB1C]/10' : 'border-white/10 hover:bg-white/5'}`}>
                  <i className="fa-solid fa-circle-play" style={{ color: '#FAAB1C' }} />
                  <span className="font-bold">samaritan_{k}_{a}.mp4</span>
                  <i className="fa-solid fa-arrow-up-right-from-square ml-auto text-xs text-white/40" />
                </a>
              )))}
            </div>
            <p className="text-[11px] text-white/40 mt-4">Burned-in captions · AI voiceover · original royalty-free music bed ducked under VO · navy end-card: “Get your FREE roof design” + (833) 207-0707. Posters + .srt sidecars ship with every cut.</p>
          </div>
        </aside>
      </main>
    </div>
  );
}
